import { ChangeEvent, useMemo, useState } from "react";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { FormattedIdentityProviderCompanyIDPInfoParam, IdentityProviderUrlType } from "@idw-types/data/identity-providers";
import { SelectOption } from "@idw-types/common";
import { InputFormGroup } from "../../common/input-form-group/input-form-group";
import { SelectFormGroup } from "../../common/select-form-group/select-form-group";
import styles from "../identity-provider-shared-style.less";
import { ButtonWithIcon } from "../../common/button-with-icon/button-with-icon";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";

type IdentityProviderCustomUrlParameterContainerProps = {
    idPrefix: string
    index: number;
    parameter: FormattedIdentityProviderCompanyIDPInfoParam;
    urlTypes: Array<IdentityProviderUrlType>;
    isDisabled: boolean;
    handleKeyChange: (newValue: string, index: number) => void;
    handleValueChange: (newValue: string, index: number) => void;
    handleUrlTypeChange: (newValue: SelectOption, index: number) => void;
    handleDeleteClick: (index: number) => void;
};


export const IdentityProviderCustomUrlParameterContainer = (props: IdentityProviderCustomUrlParameterContainerProps) => {
    const { index, parameter, urlTypes, isDisabled, idPrefix, handleKeyChange, handleValueChange, handleUrlTypeChange, handleDeleteClick } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);

    const handleKeyChangeInternal = (event: ChangeEvent<HTMLInputElement>) => {
        handleKeyChange(event.target.value, index);
    };

    const handleValueChangeInternal = (event: ChangeEvent<HTMLInputElement>) => {
        handleValueChange(event.target.value, index);
    };

    const handleUrlTypeChangeInternal = (newValue: SelectOption) => {
        handleUrlTypeChange(newValue, index);
    };

    const handleDeleteClickInternal = () => {
        handleDeleteClick(index);
    };

    const urlTypeOptions = useMemo(() => urlTypes.map(urlTypes => ({
        label: urlTypes.value,
        value: urlTypes.key
    })), [urlTypes]);

    const selectedUrlType = useMemo(() => {
        if (parameter.urlType) {
            const urlTypeOption = urlTypeOptions.find(urlType => urlType.value === parameter.urlType);
            if (urlTypeOption) {
                return urlTypeOption;
            }
        }
        return null;
    }, [parameter]);

    const resource = resources.pages.identityProviderDetails.tabs.urlParameters;

    return (
        <div className={styles.infoParamContainer}>
            <InputFormGroup
                className={styles.infoParamInputFormGroup}
                controlId={`info-param-container-key-input-${index}`}
                automationId={`info-param-container-key-input-${index}`}
                labelWidth={labelWidth}
                placeholder={resource.inputs.parameterKey.placeholder}
                ariaLabel={resource.inputs.parameterKey.label}
                disabled={isDisabled}
                isRequired={true}
                label={resource.inputs.parameterKey.label}
                value={parameter.key || ""}
                onChange={handleKeyChangeInternal}
            />
            <InputFormGroup
                className={styles.infoParamInputFormGroup}
                controlId={`info-param-container-value-input-${index}`}
                automationId={`info-param-container-value-input-${index}`}
                labelWidth={labelWidth}
                placeholder={resource.inputs.parameterValue.placeholder}
                ariaLabel={resource.inputs.parameterValue.label}
                disabled={isDisabled}
                isRequired={true}
                label={resource.inputs.parameterValue.label}
                value={parameter.value || ""}
                onChange={handleValueChangeInternal}
            />
            <SelectFormGroup
                className={styles.infoParamSelectFormGroup}
                ariaLabel={resource.inputs.urlType.label}
                automationId={`info-param-container-url-type-select-${index}`}
                controlId={`info-param-container-url-type-select-${index}`}
                disabled={isDisabled}
                isClearable={false}
                isRequired={true}
                isSearchable={false}
                label={resource.inputs.urlType.label}
                labelWidth={labelWidth}
                options={urlTypeOptions}
                onChange={handleUrlTypeChangeInternal}
                selectedOption={selectedUrlType}
            />
            <ButtonWithIcon
                id={`delete-${idPrefix}-button`}
                icon={faTrash}
                title={resources.common.buttons.delete.label}
                onClick={handleDeleteClickInternal}
                disabled={isDisabled}
                variant={"outline-primary"}
                className={styles.trashIconButton}
            />
        </div>
    );
};